/**
 * Align & distribute bar (UX Pack F4c) — a DOCKED island above the canvas,
 * shown while a freeform-block multi-selection is live. Docked rather than
 * selection-following on purpose: no scroll/zoom rect tracking, and no
 * collision with the per-section glass island (which hides under the
 * SECTION-level multi-select attr — a different store).
 *
 * One operation = measure-on-click (requestId-matched, timeout-bounded) →
 * pure geometry (shared/align-distribute.js) → ONE store write
 * (applyElementOverridesPerEid) = one undo entry, one collab op. Rects are
 * RAW iframe CSS px by contract (margins are written in CSS px). A response
 * arriving after the state or selection changed is DROPPED with a toast —
 * stale geometry never writes.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { bridge } from '../editing/bridge.js';
import { createButton } from './components.js';
import { icon } from './icons.js';
import {
  getSelectedElementUnitEids,
  getElementSelectionContext
} from '../editing/element-selection.js';
import { applyElementOverridesPerEid } from '../editing/section-utils.js';
import { getResponsiveOverrideViewport } from './viewport-controls.js';
import { FREEFORM_BLOCK_KIND } from '../editing/adapters/freeform-block-adapter.js';
import {
  computeAlignChanges,
  computeDistributeChanges,
  DISTRIBUTE_ERROR_COPY
} from '../shared/align-distribute.js';

const MEASURE_TIMEOUT_MS = 1500;
let _requestSeq = 0;
let _installed = false;

function queryRects(instanceId, eids) {
  const requestId = `align-${++_requestSeq}`;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      off();
      reject(new Error('The canvas did not answer the measurement request'));
    }, MEASURE_TIMEOUT_MS);
    const off = eventBus.on('iframe:rects-for-eids-result', (data) => {
      if (data?.requestId !== requestId) return;
      clearTimeout(timer);
      off();
      resolve(data);
    });
    bridge.send('query-rects-for-eids', { instanceId, eids, requestId });
  });
}

/**
 * @param {{mount: Element, showToast?: Function, showUndoToast?: Function,
 *   getSelectionContext?: Function, getSelectedEids?: Function,
 *   measureRects?: Function, getRevision?: Function, getViewport?: Function,
 *   applyOverrides?: Function}} deps
 * @returns {() => void} uninstall
 */
export function initElementAlignBar({
  mount,
  showToast = null,
  showUndoToast = null,
  getSelectionContext = getElementSelectionContext,
  getSelectedEids = getSelectedElementUnitEids,
  measureRects = queryRects,
  getRevision = () => store.getRevision(),
  getViewport = getResponsiveOverrideViewport,
  applyOverrides = applyElementOverridesPerEid
} = {}) {
  if (_installed || !mount) return () => {};
  _installed = true;

  const bar = document.createElement('div');
  bar.className = 'pb-align-bar';
  bar.setAttribute('role', 'toolbar');
  bar.setAttribute('aria-label', 'Align and distribute blocks');
  bar.hidden = true;

  const countEl = document.createElement('span');
  countEl.className = 'pb-align-bar-count';
  bar.appendChild(countEl);

  /**
   * Measure the CURRENT selection, validating that neither the state revision
   * nor the selection changed across the await — stale geometry never writes.
   * Skip-and-disclose: unmeasurable eids are dropped with a toast, never
   * guessed at.
   */
  async function measureSelection(minCount) {
    const context = getSelectionContext();
    const eids = getSelectedEids();
    if (!context || context.adapterKind !== FREEFORM_BLOCK_KIND || eids.length < minCount) {
      return null;
    }
    const revisionBefore = getRevision();
    const viewport = getViewport();
    const result = await measureRects(context.instanceId, eids);
    if (getRevision() !== revisionBefore || getSelectedEids().join('') !== eids.join('')) {
      showToast?.('The canvas changed while measuring — try again', { type: 'info' });
      return null;
    }
    if (Array.isArray(result.missing) && result.missing.length) {
      showToast?.(
        `${result.missing.length} block${result.missing.length === 1 ? '' : 's'} could not be measured and ${result.missing.length === 1 ? 'was' : 'were'} skipped`,
        { type: 'info' }
      );
    }
    if (!Array.isArray(result.items) || result.items.length < minCount) {
      showToast?.(DISTRIBUTE_ERROR_COPY[minCount >= 3 ? 'need-three' : 'need-two'], {
        type: 'info'
      });
      return null;
    }
    return { context, result, viewport };
  }

  async function alignSelection(side) {
    const measured = await measureSelection(2);
    if (!measured) return;
    const { context, result, viewport } = measured;
    const out = computeAlignChanges(result.items, side);
    if (out.error) {
      showToast?.(DISTRIBUTE_ERROR_COPY[out.error] || 'Cannot align this selection', {
        type: 'info'
      });
      return;
    }
    const count = Object.keys(out.changesByEid).length;
    applyOverrides(context.instanceId, out.changesByEid, {
      viewport,
      label: `Align ${count} blocks ${side}`,
      source: 'align-bar'
    });
    (showUndoToast || showToast)?.(`Aligned ${count} blocks`, { id: 'undo-align-blocks' });
  }

  async function distributeSelection() {
    const measured = await measureSelection(3);
    if (!measured) return;
    const { context, result, viewport } = measured;
    const out = computeDistributeChanges(result.items, result.parent, {
      contiguous: result.contiguous,
      sameParent: result.sameParent
    });
    if (out.error) {
      showToast?.(DISTRIBUTE_ERROR_COPY[out.error] || 'Cannot distribute this selection', {
        type: 'info'
      });
      return;
    }
    applyOverrides(context.instanceId, out.changesByEid, {
      viewport,
      label: 'Distribute blocks vertically',
      source: 'align-bar'
    });
    (showUndoToast || showToast)?.(`Equal spacing applied (${out.gapPx}px)`, {
      id: 'undo-distribute-blocks'
    });
  }

  const alignButton = (side, glyph, label) =>
    createButton({
      variant: 'ghost',
      size: 'sm',
      icon: icon(glyph),
      label,
      labelClass: 'pb-align-bar-btn-label',
      ariaLabel: `Align selected blocks ${side}`,
      testid: `align-${side}`,
      className: `pb-align-${side}`,
      errorScope: 'Align blocks',
      onAsyncClick: () => alignSelection(side)
    });

  bar.appendChild(alignButton('left', 'alignLeft', 'Left'));
  bar.appendChild(alignButton('center', 'alignCenterH', 'Center'));
  bar.appendChild(alignButton('right', 'alignRight', 'Right'));

  const separator = document.createElement('span');
  separator.className = 'pb-align-bar-sep';
  bar.appendChild(separator);

  const distributeBtn = createButton({
    variant: 'ghost',
    size: 'sm',
    icon: icon('distributeV'),
    label: 'Distribute',
    labelClass: 'pb-align-bar-btn-label',
    ariaLabel: 'Distribute selected blocks vertically',
    testid: 'align-distribute',
    className: 'pb-align-distribute',
    errorScope: 'Distribute blocks',
    onAsyncClick: () => distributeSelection()
  });
  bar.appendChild(distributeBtn);

  mount.appendChild(bar);

  const offSelection = eventBus.on('element-selection:changed', (change) => {
    const context = change?.context;
    const size = Array.isArray(change?.ids) ? change.ids.length : 0;
    const show = context?.adapterKind === FREEFORM_BLOCK_KIND && size >= 2;
    bar.hidden = !show;
    if (!show) return;
    countEl.textContent = `${size} blocks`;
    const canDistribute = size >= 3;
    distributeBtn.disabled = !canDistribute;
    distributeBtn.title = canDistribute ? '' : DISTRIBUTE_ERROR_COPY['need-three'];
  });

  return () => {
    offSelection();
    bar.remove();
    _installed = false;
  };
}
